import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Form, Input} from 'antd';
import TableAntD from "@common/TableAntD";
import PageHeader from "@common/PageHeader";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import dayjs from 'dayjs';
import Button from "@common/Button";
import Tag from "@common/Tag";
import {ArrowForwardRounded, AutoAwesomeRounded, EditRounded, CheckRounded, CloseRounded} from "@mui/icons-material";
import axios from "@common/axios";
import useCurrentShortcut from "@hooks/useCurrentShortcut";

const LessonPlannerLessonPlans = () => {
    const { loadShortcutByUrl } = useCurrentShortcut();
    const [form] = Form.useForm();
    const [filters, setFilters] = useState({ name: '' });
    const [lessonPlans, setLessonPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [firstSchedule, setFirstSchedule] = useState(undefined);
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const editingNameRef = useRef('');

    // Fetch lesson plans from backend
    useEffect(() => {
        setLoading(true);
        Promise.all([
            axios.get('/web/ui/lessonPlanner/lessonPlans'),
            axios.get('/web/ui/lessonScheduler/plans')
        ])
            .then(([plansRes, schedulesRes]) => {
                console.log('Fetched lesson plans:', plansRes.data);
                setLessonPlans(plansRes.data || []);
                const schedules = schedulesRes.data;
                setFirstSchedule(schedules && schedules.length > 0 ? schedules[0] : null);
            })
            .catch(err => {
                console.error('Error fetching lesson plans:', err);
                setLessonPlans([]);
                setFirstSchedule(null);
            })
            .finally(() => setLoading(false));
    }, []);

    // Transform backend DTOs to match frontend table structure
    const transformedPlans = useMemo(() => {
        const transformed = lessonPlans.map(plan => ({
            id: plan.lessonPlanId,
            name: plan.lessonName || plan.contentName,
            sections: plan.concepts?.length || 0,
            createdDate: plan.createDate,
            associatedSchedules: plan.associatedLessons || [], // Array of {lessonId, lessonName, scheduleId, scheduleName}
            conceptName: plan.conceptName,
            topicName: plan.topicName,
            publishState: plan.publishState,
            generationStatus: plan.generationStatus
        }));
        console.log('Transformed plans:', transformed);
        return transformed;
    }, [lessonPlans]);

    const filteredData = useMemo(() => {
        return transformedPlans.filter(row => {
            if (filters.name && !row.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            return true;
        });
    }, [filters, transformedPlans]);

    const handleValuesChange = (_, all) => setFilters({ name: all.name || '' });

    const handleRename = useCallback((lessonPlanId) => {
        const newName = editingNameRef.current;
        console.log('handleRename called, lessonPlanId:', lessonPlanId, 'newName from ref:', newName);

        if (!newName || newName.trim() === '') {
            displayToastMessage('Invalid name', 'Lesson plan name cannot be empty', 'warning');
            return;
        }

        const payload = { lessonName: newName };
        console.log('Sending PUT request:', `/web/ui/lessonPlanner/lessonPlans/${lessonPlanId}`, 'payload:', payload);

        axios.put(`/web/ui/lessonPlanner/lessonPlans/${lessonPlanId}`, payload)
            .then(() => {
                console.log('Rename successful');
                // Update local state
                setLessonPlans(prevPlans => prevPlans.map(plan =>
                    plan.lessonPlanId === lessonPlanId ? { ...plan, lessonName: newName } : plan
                ));
                setEditingId(null);
                setEditingName('');
                editingNameRef.current = '';
                displayToastMessage('Renamed', 'Lesson plan renamed successfully', 'success');
            })
            .catch(error => {
                console.error('Error renaming lesson plan:', error);
                displayToastMessage('Rename failed', error.response?.data?.message || 'Failed to rename lesson plan', 'error');
                setEditingId(null);
                setEditingName('');
                editingNameRef.current = '';
            });
    }, []);

    const startEditing = useCallback((record) => {
        console.log('startEditing called with record:', record);
        setEditingId(record.id);
        setEditingName(record.name);
        editingNameRef.current = record.name;
        console.log('Set editingNameRef.current to:', record.name);
    }, []);

    const cancelEditing = useCallback(() => {
        setEditingId(null);
        setEditingName('');
        editingNameRef.current = '';
    }, []);

    const handleEditingNameChange = useCallback((e) => {
        const value = e.target.value;
        console.log('handleEditingNameChange called, new value:', value);
        setEditingName(value);
        editingNameRef.current = value;
        console.log('Updated editingNameRef.current to:', value);
    }, []);

    const handleKeyDown = useCallback((e, lessonPlanId) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleRename(lessonPlanId);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditing();
        }
    }, [handleRename, cancelEditing]);

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
                const isEditing = editingId === record.id;

                if (isEditing) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Input
                                value={editingName}
                                onChange={handleEditingNameChange}
                                onKeyDown={(e) => handleKeyDown(e, record.id)}
                                autoFocus
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="text"
                                icon={<CheckRounded />}
                                onClick={() => handleRename(record.id)}
                                size="small"
                            />
                            <Button
                                type="text"
                                icon={<CloseRounded />}
                                onClick={cancelEditing}
                                size="small"
                            />
                        </div>
                    );
                }

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Button
                            icon={<ArrowForwardRounded />}
                            iconPosition={'right'}
                            type={'text'}
                            onClick={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonPlans/plan/${record.id}`)}
                            style={{ flex: 1, justifyContent: 'flex-start' }}
                        >
                            <strong>{text}</strong>
                        </Button>
                        <Button
                            type="text"
                            icon={<EditRounded />}
                            onClick={(e) => {
                                e.stopPropagation();
                                startEditing(record);
                            }}
                            size="small"
                        />
                    </div>
                );
            },
        },
        {
            title: 'Sections',
            dataIndex: 'sections',
            key: 'sections',
            width: 90,
        },
        {
            title: 'Created Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 140,
            render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Associated Schedules',
            dataIndex: 'associatedSchedules',
            key: 'associatedSchedules',
            render: (schedules) => {
                if (!schedules || schedules.length === 0) {
                    return <span style={{color: '#999', fontStyle: 'italic'}}>None</span>;
                }

                // Group by schedule to avoid duplicates
                const uniqueSchedules = schedules.reduce((acc, lesson) => {
                    if (!acc.some(s => s.scheduleId === lesson.scheduleId)) {
                        acc.push(lesson);
                    }
                    return acc;
                }, []);

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {uniqueSchedules.map(lesson => (
                            <Tag
                                key={lesson.scheduleId}
                                style={{ cursor: 'pointer' }}
                                onClick={() => loadShortcutByUrl(`/web/ui/view/lessonStudio/lessonSchedules/schedule/${lesson.scheduleId}?lessonId=${lesson.lessonId}`)}
                            >
                                {lesson.scheduleName}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
    ], [firstSchedule, loadShortcutByUrl, editingId, editingName, handleRename, handleEditingNameChange, handleKeyDown, cancelEditing, startEditing]);

    return (
        <div>
            <PageHeader
                title={'Lesson Plans'}
                subtitle={'View and manage your individually generated lesson plans.'}
                buttons={
                    <>
                        <Button icon={<AutoAwesomeRounded />} onClick={() => loadShortcutByUrl('/web/ui/view/lessonStudio/lessonPlans/new')}>New Lesson Plan</Button>
                    </>
                }
            />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm form={form} onValuesChange={handleValuesChange}>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item name="name" label="Name">
                                <Input placeholder="Search by name" allowClear />
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                }
            />
            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                data={filteredData}
                loading={loading}
                scroll={{x: true}}
                rowKey="id"
                locale={{emptyText: 'No individual plans'}}
            />
        </div>
    );
};

export default LessonPlannerLessonPlans;
